import type {
  PublicVisitorAvailability
} from '../../client'
import type {
  BusyInterval
} from './schedulingUtils'
import {
  chunkHorizon
} from './schedulingUtils'

// Provider adapters for the visitor-availability overlay ("show times you're
// free"): the visitor grants read-only calendar access in a popup (Google GIS
// token flow / Microsoft MSAL), we query their busy intervals over the slot
// horizon in the browser. Nothing here talks to our backend, and no token
// reaches it — the hook persists the grant via visitorAvailabilitySession.ts
// so a reload or a second link doesn't re-prompt.

export type AvailabilityProvider = 'google' | 'microsoft'

export interface VisitorIdentity {
  email: string
  name: string | null
}

// Authorization and the free/busy read are separate because a restored
// session already holds a token: it skips straight to fetchBusy, which is what
// makes a refresh (or a second link) popup-free.
export interface VisitorAuthorization {
  identity: VisitorIdentity
  accessToken: string
  /** Epoch ms the token stops working. */
  expiresAt: number
}

export interface VisitorBusyResult {
  busyIntervals: BusyInterval[]
  // True when a provider read was cut short (Graph page cap), i.e. some
  // slots may show a false green dot. Analytics-only today.
  truncated: boolean
}

export interface Horizon {
  start: Date
  end: Date
}

// The visitor closed/denied the consent popup — an expected outcome, reported
// separately from real failures so analytics can tell the two apart.
export class AvailabilityDeclinedError extends Error {}

// The grant itself is gone — revoked, or the token died before its stated
// expiry. Distinguished from a transient read failure so a flaky network or a
// provider 5xx doesn't cost the visitor a fresh consent popup for a token that
// still works.
export class AvailabilityAuthError extends Error {}

// 401 only. 403 is NOT reliably a dead grant: Google Calendar returns it for
// rateLimitExceeded / userRateLimitExceeded / quotaExceeded — throttling, with
// a perfectly live token — so treating it as revoked would charge a fresh
// popup for the exact case this split exists to survive, and inflate the
// grant_revoked rate the split is judged on. Graph's dead-token signal
// (InvalidAuthenticationToken) is a 401 too. Anything ambiguous belongs on the
// transient side: the cost of keeping a token that keeps failing is one idle
// connect strip per pageload, against a consent popup the visitor didn't owe.
function busyReadError(what: string, status: number): Error {
  const message = `${what} failed (${status})`
  return status === 401
    ? new AvailabilityAuthError(message)
    : new Error(message)
}

// The page the MSAL popup redirects to (frontend/msal-popup.html, a second
// Vite entry — it runs MSAL's redirect bridge, so it can't be a static
// public/ file). Must be registered as an SPA redirect URI in the Azure app
// registration.
export const MSAL_POPUP_REDIRECT_PATH = '/msal-popup.html'

const GOOGLE_SCOPES = 'openid email profile https://www.googleapis.com/auth/calendar.readonly'
const GIS_SCRIPT_URL = 'https://accounts.google.com/gsi/client'
const MICROSOFT_SCOPES = ['User.Read', 'Calendars.Read']

// Both providers can omit an expiry — GIS leaves out `expires_in`, MSAL leaves
// `expiresOn` null on a cache-only result — and both issue an hour in
// practice. The session store's skew covers the edges.
const DEFAULT_TOKEN_LIFETIME_MS = 3600_000

let gisScriptPromise: Promise<void> | null = null

function loadGisScript(): Promise<void> {
  if (window.google?.accounts?.oauth2) return Promise.resolve()
  gisScriptPromise ??= new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = GIS_SCRIPT_URL
    script.async = true
    script.onload = () => resolve()
    script.onerror = () => {
      gisScriptPromise = null
      reject(new Error('Failed to load Google sign-in'))
    }
    document.head.appendChild(script)
  })
  return gisScriptPromise
}

function requestGoogleAccessToken(
  clientId: string
): Promise<{ accessToken: string, expiresAt: number }> {
  return new Promise((resolve, reject) => {
    const oauth2 = window.google?.accounts?.oauth2
    if (!oauth2) {
      reject(new Error('Google sign-in unavailable'))
      return
    }
    const client = oauth2.initTokenClient({
      client_id: clientId,
      scope: GOOGLE_SCOPES,
      callback: (response) => {
        if (response.access_token) {
          resolve({
            accessToken: response.access_token,
            expiresAt: response.expires_in
              ? Date.now() + response.expires_in * 1000
              : Date.now() + DEFAULT_TOKEN_LIFETIME_MS,
          })
        } else if (response.error === 'access_denied') {
          reject(new AvailabilityDeclinedError('Consent denied'))
        } else {
          reject(new Error(response.error_description ?? response.error ?? 'No access token'))
        }
      },
      error_callback: (error) => {
        if (error.type === 'popup_closed') {
          reject(new AvailabilityDeclinedError('Popup closed'))
        } else {
          reject(new Error(error.message ?? 'Google sign-in failed'))
        }
      },
    })
    client.requestAccessToken()
  })
}

async function fetchGoogleIdentity(accessToken: string): Promise<VisitorIdentity> {
  const resp = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
    headers: { Authorization: `Bearer ${accessToken}` },
  })
  if (!resp.ok) throw new Error(`userinfo failed (${resp.status})`)
  const data = await resp.json() as { email?: string, name?: string }
  if (!data.email) throw new Error('userinfo returned no email')
  return { email: data.email, name: data.name ?? null }
}

async function fetchGoogleBusyChunk(
  accessToken: string,
  chunk: Horizon
): Promise<BusyInterval[]> {
  const resp = await fetch('https://www.googleapis.com/calendar/v3/freeBusy', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      timeMin: chunk.start.toISOString(),
      timeMax: chunk.end.toISOString(),
      // Known v1 limitation: only the primary calendar is checked, so events
      // on secondary calendars render as free. Widening needs a calendarList
      // read + one freeBusy item per calendar.
      items: [{ id: 'primary' }],
    }),
  })
  if (!resp.ok) throw busyReadError('freeBusy', resp.status)
  const data = await resp.json() as {
    calendars?: { primary?: { busy?: Array<{ start: string, end: string }> } }
  }
  return data.calendars?.primary?.busy ?? []
}

async function authorizeGoogle(clientId: string): Promise<VisitorAuthorization> {
  await loadGisScript()
  // Deliberately NOT revoked on disconnect: this is the same Google OAuth
  // client as login and calendar-connect, and revocation reaches the whole
  // (user, client) grant — a visitor who is also a Catch user with a
  // connected calendar would lose that grant.
  const { accessToken, expiresAt } = await requestGoogleAccessToken(clientId)
  const identity = await fetchGoogleIdentity(accessToken)
  return { identity, accessToken, expiresAt }
}

async function fetchGoogleBusy(
  accessToken: string,
  horizon: Horizon
): Promise<VisitorBusyResult> {
  const busyChunks = await Promise.all(
    chunkHorizon(horizon).map((chunk) => fetchGoogleBusyChunk(accessToken, chunk))
  )
  return { busyIntervals: busyChunks.flat(), truncated: false }
}

// Graph datetimes come back as offset-less strings in the requested timezone
// ("2030-01-15T10:00:00.0000000"); we request UTC, so brand them Z-suffixed
// so Date.parse doesn't read them as local wall-clock.
function graphDateTimeToIso(value: string): string {
  return /[zZ]$|[+-]\d{2}:\d{2}$/.test(value) ? value : `${value}Z`
}

// Statuses that actually block a meeting — an allowlist, mirroring
// microsoft_calendar.py's, so an absent or 'unknown' showAs reads as free
// rather than painting the whole page red.
const GRAPH_BLOCKING_SHOW_AS = new Set(['busy', 'tentative', 'oof'])

// Page cap per chunk, mirroring the backend's MS_GRAPH_MAX_EVENT_PAGES: a
// dense recurring calendar must not stall the spinner on dozens of
// sequential round-trips. Truncation just renders later slots free.
const GRAPH_MAX_EVENT_PAGES = 25

export interface GraphCalendarViewEvent {
  start?: { dateTime?: string }
  end?: { dateTime?: string }
  showAs?: string
}

// Pure so the allowlist and UTC-branding are unit-testable.
export function graphEventsToBusyIntervals(
  events: GraphCalendarViewEvent[]
): BusyInterval[] {
  const intervals: BusyInterval[] = []
  for (const event of events) {
    const start = event.start?.dateTime
    const end = event.end?.dateTime
    if (!start || !end) continue
    if (!GRAPH_BLOCKING_SHOW_AS.has(event.showAs ?? '')) continue
    intervals.push({
      start: graphDateTimeToIso(start),
      end: graphDateTimeToIso(end),
    })
  }
  return intervals
}

// calendarView (not getSchedule): getSchedule needs Calendars.Read.Shared and
// is unsupported for personal Microsoft accounts — a big slice of public
// booking-page visitors — while calendarView works with Calendars.Read on
// both account types. It expands recurrences server-side; we page via
// @odata.nextLink. Chunking bounds each page walk (and matches Google's cap).
async function fetchMicrosoftBusyChunk(
  accessToken: string,
  chunk: Horizon
): Promise<{ intervals: BusyInterval[], truncated: boolean }> {
  const intervals: BusyInterval[] = []
  const params = new URLSearchParams({
    startDateTime: chunk.start.toISOString(),
    endDateTime: chunk.end.toISOString(),
    $select: 'start,end,showAs',
    $top: '100',
  })
  let url: string | null =
    `https://graph.microsoft.com/v1.0/me/calendarView?${params.toString()}`
  let pages = 0
  while (url && pages < GRAPH_MAX_EVENT_PAGES) {
    pages += 1
    const resp: Response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        // Graph defaults calendarView to UTC anyway; kept so the UTC
        // assumption graphDateTimeToIso brands onto the values is explicit
        // (Authorization already forces the CORS preflight).
        Prefer: 'outlook.timezone="UTC"',
      },
    })
    if (!resp.ok) throw busyReadError('calendarView', resp.status)
    const data = await resp.json() as {
      value?: GraphCalendarViewEvent[]
      '@odata.nextLink'?: string
    }
    intervals.push(...graphEventsToBusyIntervals(data.value ?? []))
    url = data['@odata.nextLink'] ?? null
  }
  // Cap hit with pages remaining ⇒ the un-walked window renders free; the
  // caller reports it (AVAILABILITY_MARKED's `truncated`) so a false green
  // dot is at least measurable.
  return { intervals, truncated: url !== null }
}

// MSAL's own cache stays in memory: the persisted session
// (visitorAvailabilitySession.ts) is the single store both providers share, so
// a restore takes the same fetchBusy path regardless of who issued the token.
async function authorizeMicrosoft(clientId: string): Promise<VisitorAuthorization> {
  // Lazy import keeps MSAL out of the main bundle; only booking-page visitors
  // who click the Microsoft button ever download it.
  const {
    PublicClientApplication, BrowserAuthError, ServerError
  } = await import('@azure/msal-browser')
  const app = new PublicClientApplication({
    auth: {
      clientId,
      // A blank static page, NOT the SPA: the app router would bounce the
      // popup to /login and strip the auth response before the opener's MSAL
      // poller reads it, leaving the popup open.
      redirectUri: `${window.location.origin}${MSAL_POPUP_REDIRECT_PATH}`,
    },
    cache: {
      // MSAL's cache stays per-pageload; persistence across reloads is the
      // shared session store's job, not a second provider-specific one.
      cacheLocation: 'memoryStorage',
    },
  })
  await app.initialize()
  let auth
  try {
    auth = await app.acquireTokenPopup({ scopes: MICROSOFT_SCOPES, prompt: 'select_account' })
  } catch (err) {
    // Two distinct declines. Closing the popup surfaces as MSAL's own
    // BrowserAuthError; declining consent on the Microsoft page comes back as
    // AAD's `error=access_denied`, which MSAL only translates when it carries
    // subError `user_cancelled` (InteractionHandler.handleCodeResponse) — AAD
    // sends no subError here, so it arrives as a bare ServerError and would
    // otherwise render the red "something went wrong" state.
    if (err instanceof BrowserAuthError && err.errorCode === 'user_cancelled') {
      throw new AvailabilityDeclinedError('Popup closed')
    }
    if (err instanceof ServerError && err.errorCode === 'access_denied') {
      throw new AvailabilityDeclinedError('Consent denied')
    }
    throw err
  }
  const email = auth.account.username
  if (!email) throw new Error('Microsoft account has no email')
  return {
    identity: { email, name: auth.account.name ?? null },
    accessToken: auth.accessToken,
    expiresAt: auth.expiresOn?.getTime() ?? Date.now() + DEFAULT_TOKEN_LIFETIME_MS,
  }
}

async function fetchMicrosoftBusy(
  accessToken: string,
  horizon: Horizon
): Promise<VisitorBusyResult> {
  const busyChunks = await Promise.all(
    chunkHorizon(horizon).map((chunk) => fetchMicrosoftBusyChunk(accessToken, chunk))
  )
  return {
    busyIntervals: busyChunks.flatMap((c) => c.intervals),
    truncated: busyChunks.some((c) => c.truncated),
  }
}

export interface VisitorCalendarProvider {
  id: AvailabilityProvider
  label: string
  clientIdOf: (config: PublicVisitorAvailability) => string | null
  /** Opens the consent popup. Only ever called from a click. */
  authorize: (clientId: string) => Promise<VisitorAuthorization>
  /** Reads free/busy with an already-granted token — the restore path. */
  fetchBusy: (accessToken: string, horizon: Horizon) => Promise<VisitorBusyResult>
}

// The single provider table — buttons, client-id lookup and connectors all
// derive from it, so a third provider is one entry here.
export const VISITOR_CALENDAR_PROVIDERS: VisitorCalendarProvider[] = [
  {
    id: 'google',
    label: 'Connect Google Calendar',
    clientIdOf: (config) => config.google_client_id ?? null,
    authorize: authorizeGoogle,
    fetchBusy: fetchGoogleBusy,
  },
  {
    id: 'microsoft',
    label: 'Connect Microsoft Calendar',
    clientIdOf: (config) => config.microsoft_client_id ?? null,
    authorize: authorizeMicrosoft,
    fetchBusy: fetchMicrosoftBusy,
  },
]

export function providerById(
  id: AvailabilityProvider
): VisitorCalendarProvider | undefined {
  return VISITOR_CALENDAR_PROVIDERS.find((p) => p.id === id)
}

export function availableProviders(
  config: PublicVisitorAvailability
): VisitorCalendarProvider[] {
  return VISITOR_CALENDAR_PROVIDERS.filter((p) => p.clientIdOf(config) !== null)
}
